import {
  createTheme,
  Theme,
  TypographyVariantsOptions as TypographyOptions,
} from "@mui/material/styles";

const defaultTheme: Theme = createTheme();

/**
 * The platform's single, locked type scale: Manrope for every heading role
 * (`h1`–`h6`, plus `subtitle1`/`subtitle2`/`button`) and Lato for everything
 * that reads as body or UI text (`body1`, `body2`, `label`, `caption`,
 * `overline`). This file is the only place font choices are made, and every
 * value below is stated exactly, not derived from a ratio or a prior ramp.
 *
 * `label` is a platform-spec addition, not a MUI-standard variant (see the
 * augmentation in `src/mui.d.ts`). It carries the old pinned `subtitle1`
 * value (Lato 13/700) forward, unifying what used to be two separate
 * grandfathered exceptions — the pre-spec `subtitle1` (~15 call sites) and
 * `subtitle2` (~5 call sites) — into one "field-label / status line" role,
 * now that `subtitle1`/`subtitle2` themselves take on the spec's larger
 * Manrope sizes. Every former `subtitle1`/`subtitle2` call site was migrated
 * to `variant="label"` so none of them silently changed size.
 */
export const DISPLAY_FONT = "'Manrope', 'Lato', sans-serif";
const BODY_FONT = "'Lato', 'Roboto', sans-serif";

/**
 * No variant states `textAlign`. Eight of them used to carry
 * `textAlign: "left"` — defensively, against a global `text-align: center` that
 * no longer exists anywhere in `src/theme` or `src/css`. What it does now is
 * break every centred container: a variant's own class outranks the inherited
 * `text-align`, so a `Typography` inside a `textAlign: "center"` panel silently
 * went back to left while its unstyled siblings stayed centred (the AI critique
 * empty state read exactly that way — left title over centred body). Alignment
 * belongs to the box, so the box keeps it.
 */
const typography = {
  fontFamily: "'Lato', 'Roboto', sans-serif",
  /**
   * these typography values can be applied by 'light', 'regular', 'medium' and 'bold'
   * Usage:
   * =====
   *
     <Box sx={{ fontWeight: 'light' }}>
      theme.typography.fontWeightLight
     </Box>

     <Box sx={{ fontWeight: 'regular' }}>
      theme.typography.fontWeightRegular
     </Box>

     <Box sx={{ fontWeight: 'medium' }}>
      theme.typography.fontWeightMedium
     </Box>

     <Box sx={{ fontWeight: 500 }}>
       custom values
     </Box>

     <Box sx={{ fontWeight: 'bold' }}>
        theme.typography.fontWeightMedium
     </Box>
   *
   */
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  /** Page title. Manrope 700, 40px. */
  h1: {
    fontFamily: DISPLAY_FONT,
    fontSize: defaultTheme.typography.pxToRem(40),
    fontWeight: 700,
    lineHeight: 1.1,
  },
  /** Section heading and the hub header band's title. Manrope 700, 30px. */
  h2: {
    fontFamily: DISPLAY_FONT,
    fontSize: defaultTheme.typography.pxToRem(30),
    fontWeight: 700,
    lineHeight: 1.15,
  },
  /** Card and in-page section title. Manrope 600, 26px. */
  h3: {
    fontFamily: DISPLAY_FONT,
    fontSize: defaultTheme.typography.pxToRem(26),
    fontWeight: 600,
    lineHeight: 1.25,
  },
  /** Row / UI title. Manrope 600, 22px. */
  h4: {
    fontFamily: DISPLAY_FONT,
    fontSize: defaultTheme.typography.pxToRem(22),
    fontWeight: 600,
    lineHeight: 1.3,
  },
  /** Fifth heading step. Manrope 600, 19px. */
  h5: {
    fontFamily: DISPLAY_FONT,
    fontSize: defaultTheme.typography.pxToRem(19),
    fontWeight: 600,
    lineHeight: 1.35,
  },
  /**
   * An ordinary small heading, NOT the uppercase eyebrow — it has call sites
   * (Footer, Header, `BuilderTopicCard`) using it as a plain small heading,
   * and putting the eyebrow's uppercase and tracking on the variant would
   * shout every one of them. The eyebrow is `overline`.
   *
   * The bare-element uppercase rule (`h6:not(.MuiTypography-root)` in
   * `Override.tsx`) only ever applied to bare `<h6>` tags, never to this
   * variant, and is untouched.
   *
   * Manrope 600, 17px.
   */
  h6: {
    fontFamily: DISPLAY_FONT,
    fontSize: defaultTheme.typography.pxToRem(17),
    fontWeight: 600,
    lineHeight: 1.4,
  },
  /** Manrope 500, 16px. */
  subtitle1: {
    fontFamily: DISPLAY_FONT,
    fontSize: defaultTheme.typography.pxToRem(16),
    fontWeight: 500,
    lineHeight: 1.4,
  },
  /** Manrope 600, 14.5px. */
  subtitle2: {
    fontFamily: DISPLAY_FONT,
    fontSize: defaultTheme.typography.pxToRem(14.5),
    fontWeight: 600,
    lineHeight: 1.4,
  },
  /** Body text. Lato 400, 15px. */
  body1: {
    fontFamily: BODY_FONT,
    fontSize: defaultTheme.typography.pxToRem(15),
    fontWeight: 400,
    lineHeight: 1.5,
  },
  /** Footer / helper text. Lato 400, 13.5px. Colour comes from the `color`
   * prop, never baked in. */
  body2: {
    fontFamily: BODY_FONT,
    fontSize: defaultTheme.typography.pxToRem(13.5),
    fontWeight: 400,
    lineHeight: 1.5,
  },
  /**
   * Field-label / status-line role. Lato 700, 13px — see the file header
   * comment: this is the old pinned `subtitle1` value, given its own name.
   */
  label: {
    fontFamily: BODY_FONT,
    fontSize: defaultTheme.typography.pxToRem(13),
    fontWeight: 700,
    lineHeight: 20 / 13,
  },
  /** Button label. Manrope 600, 14px, `textTransform: none` stated
   * explicitly rather than MUI's uppercase default. */
  button: {
    fontFamily: DISPLAY_FONT,
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 600,
    lineHeight: 1.2,
    textTransform: "none",
  },
  /**
   * Field-level error. Also MUI's `FormHelperText` base style — it spreads
   * `theme.typography.caption` directly (see `FormHelperText.js`) — so every
   * field's validation message renders through this variant.
   */
  caption: {
    display: "inline-block",
    fontFamily: BODY_FONT,
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 400,
    lineHeight: 1.4,
  },
  /**
   * The eyebrow. Lato 500, 11px, 0.08em tracking, uppercase.
   *
   * `textTransform` is on the variant, unlike every other variant here: an
   * eyebrow that is not uppercase is not an eyebrow.
   */
  overline: {
    fontFamily: BODY_FONT,
    fontSize: defaultTheme.typography.pxToRem(11),
    fontWeight: 500,
    lineHeight: 1.4,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  /** Renders as whatever contains it — states its family explicitly so an
   * inherited Manrope or Lato from a parent variant carries through as-is. */
  inherit: {
    fontFamily: "inherit",
  },
} as TypographyOptions;

export default typography;
